import { DocStatus } from '@common/types';

/**
 * Types for Document Upload Customer Portal
 */
export interface AcceptedDocument {
  name: string;
  sample_file_path?: string;
  caution?: string;
  is_questionnaire?: boolean;
  questionnaire_document_type_id?: number;
}

// Customer Requirement Status
export enum CustomerRequirementStatus {
  Pending = 'pending',
  PendingVerification = 'pending_verification',
  Accepted = 'accepted',
  WaivedOff = 'waived_off'
}

// Customer Uploaded Document Status
export enum UploadedDocStatus {
  Uploaded = 'uploaded',
  SharedWithInsurer = 'shared_with_insurer',
  Approved = 'approved',
  Rejected = 'rejected',
  QuestionnaireConfirmed = 'questionnaire_confirmed'
}

// Uploaded Document info as saved in db
export interface UploadedDoc {
  id: number;
  document_type: string;
  created_at: string;
  uploaded_at: string;
  status: UploadedDocStatus;
  rejection_message?: string;
  file_name: string;
  file_path: string;
  file_type: string;
  is_questionnaire?: boolean;
}

export interface DocumentInfo {
  id?: number;
  name: string;
  size?: number;
  docType: string;
  docStatus: DocStatus;
  submitStatus?: UploadedDocStatus;
  fileKey?: string;
  uploadedAt?: string;
  progress?: number;
  cancel?: any;
  reason?: string;
  previewURL?: string;
  loaded?: number;
  isQuestionnaire?: boolean;
}

// Customer Requirement common properties among requirement and additional requirements
interface RequirementPayload {
  requirement_type: 'document' | 'link' | 'declaration';
  created_at: string;
  internal_id: string;
  description: string;
  updated_at: string;
  declaration_template?: string;
}

// Customer Requirement - Additional Requirements similar payload
export type RawAdditionalRequirementPayload = RequirementPayload;

// documents from API are grouped before transforming for FE
export type AdditionalRequirementPayload = RawAdditionalRequirementPayload & {
  documents?: Array<UploadedDoc>;
};

// Customer Requirement - Primary Requirement (only primary requirements have below properties)
export type CustomerRequirementPayload = RequirementPayload & {
  category: string;
  accepted_docs?: Array<AcceptedDocument>;
  action_url?: string;
  additional_requirements?: Array<RawAdditionalRequirementPayload>;
  status: CustomerRequirementStatus;
  documents?: Array<UploadedDoc>;
  // Member grouping fields (health flow). Optional so term payload stays compatible.
  member_id?: string;
  member_name?: string;
  relation?: string;
};

// documents in AdditionalRequirementPayload is transformed into DocumentInfo for uniformity in FE
export type AdditionalRequirement = Omit<
  AdditionalRequirementPayload,
  'documents'
> & {
  documents?: DocumentInfo[];
};

export type CustomerRequirement = Omit<
  CustomerRequirementPayload,
  'documents' | 'additional_requirements'
> & {
  // documents in CustomerRequirementPayload is transformed into DocumentInfo for uniformity in FE
  documents?: DocumentInfo[];
  // similarly documents in additional_requirements is transformed too, hence referring to transformed type `AdditionalRequirement`
  additional_requirements?: Array<AdditionalRequirement>;
};

export interface CustomerRequirements {
  requirements: Array<CustomerRequirementPayload>;
}
